import { Maybe, Money, OverallStageEnum, Scalars } from '__generated__/types';
import { TransportMode } from 'components/Modals/Enquiries/EditShippingModal';
import { User } from 'interfaces';
import { EnquiryPartPriceOption, EnquiryPartPriceOptionType, IEnquiry, ITolerance } from 'interfaces/graphql';
import { ICachedXometryVatNumber, ICurrentlyViewing } from 'interfaces/shared';
import { IShoreHardness } from 'utils/graphql/models/ShoreHardness';

import { Certificates } from './DealStore';
import { AuditState } from './PartsStore';

export enum EnquiryStates {
  draft = 'draft',
  pending = 'pending',
  needCalculate = 'need_calculate',
  calculated = 'calculated',
  submitted = 'submitted',
  ordered = 'ordered',
}

export enum EnquiryQuotingState {
  DEFAULT = 'default',
  EDITING = 'editing',
  WAITING = 'waiting',
  MODERATION = 'moderation',
  CALCULATION = 'calculation',
  CALC_FINISHED = 'calc_finished',
  SA_CHECKED = 'sa_checked',
  FINALIZED = 'finalized',
}

export enum EnquiryHumanStates {
  draft = 'Draft',
  pending = 'Pending',
  // eslint-disable-next-line camelcase
  need_calculate = 'Need Calculate',
  calculated = 'Calculated',
  submitted = 'Submitted',
  ordered = 'Ordered',
}

export enum EnquiryPartStates {
  pending = 'pending',
  processed = 'processed',
  failed = 'failed',
}

export enum EnquiryPartOption {
  standard = 'standard',
  express = 'express',
}

export interface EnquiryPartDimensions {
  x: number;
  y: number;
  z: number;
}

export interface EnquiryError {
  title: string;
}

export interface EnquiryPartFile {
  id: number;
  parentId: number;
  name: string;
  uuid: string;
  context: string;
  preview: string;
  previewMedium: string;
  previewLarge: string;
  isCleaned: boolean;
  previewPriority: number;
}

export interface EnquiryPartTolerance {
  quantity: number;
  precision: string;
}

export interface EnquiryPartSurface {
  finish: string;
  quantity: number | null;
  entirePart: boolean;
}

export interface EnquiryPartMarking {
  quantity: number;
  markingType: string;
}

export interface EnquiryPromotion {
  code: string;
  value: number;
}

export interface EnquiryFeatures {
  processes: EnquiryProcess[];
  finishes: EnquiryFinish[];
  materialTypes: EnquiryMaterialType[];
}

export interface EnquiryFeature {
  id: number;
  active: boolean;
  name: string;
  position: number;
  xometryId: string;
}

export interface EnquiryMaterialType extends EnquiryFeature {
  processId: number;
  custom: boolean;
  defaultFinishId?: string;
  defaultMaterialId?: string;
}

export interface EnquiryFinish extends EnquiryFeature {
  custom: boolean;
  processId: number;
  restrictedMaterials: number[];
  colors: { id: number; name: string }[];
  defaultColorId?: number;
}

export enum EnquiryProcessInitializm {
  CNC = 'CNC',
}

export interface EnquiryProcess extends EnquiryFeature {
  initialism: EnquiryProcessInitializm;
  colors: { id: number; name: string }[];
  defaultColorId?: number;
}

export interface EnquryFeatureOption {
  value: number;
  label: string;
}

export interface EnquiryGauge {
  mm?: string | null;
  name?: string | null;
}

export interface EnquiryMaterial {
  id: number;
  name: string;
  materialTypeId: number;
  options: {
    [key: string]: string;
  };
  hardware: boolean;
  threading: boolean;
  partMarkings: {
    default: string;
    options: EMPartMarking[];
  };
  gauges: EnquiryGauge[];
  shoreHardnesses?: IShoreHardness[];
  tolerancesNew?: ITolerance[];
  surfaceRoughness: {
    default: string;
    options: EMSurface;
  };
}

export interface EMPartMarking {
  val: string;
}

export interface EMSurface {
  [key: string]: string;
}

export interface EnquiryPartDFMFeedback {
  title: string;
  description: string;
  recommendation: string;
  dfmType: 'warning' | 'error';
  i18nKey: string;
  quantity: number;
}

export interface EnquiryAdditionalService {
  id: number;
  position: number;
  partPosition?: number;
  name: string;
  value: number;
  currency?: string;
  typeName: string;
  editable: boolean;
}

/**
 * TODO: Import this interface from codegen when backend will be ready
 */
export interface OmsEnquiryMeasurementProtocol {
  __typename?: 'OmsEnquiryMeasurementProtocol';
  allowedProcessIds: Array<Scalars['Int']>;
  createdAt: Scalars['ISO8601DateTime'];
  description?: Maybe<Scalars['String']>;
  id: Scalars['Int'];
  key: Scalars['String'];
  name: Scalars['String'];
  priceMoney?: Maybe<Money>;
  updatedAt: Scalars['ISO8601DateTime'];
}

export interface EnquiryPart {
  id: number;
  position: number;
  name: string;
  state: EnquiryPartStates;
  is2d: boolean;
  engineCalculable: boolean;
  manuallyQuoted: boolean;
  dealPartId: number;
  enquiryId: number;
  quantity: number;
  success: boolean;
  deleted: boolean;
  shouldRfq: boolean;
  noQuote: boolean;
  dimensions: EnquiryPartDimensions;
  volume: number;
  weight: number;
  weightKgPerPiece: number;
  notes?: string | null;
  internalComment?: string;
  engineerFeedback: string;
  rfqFeedback: string;
  rfqStatus?: string | null;
  dfmFeedback: EnquiryPartDFMFeedback[];

  processType: string;
  process: string;
  processId: number;
  providerPriceRate: number;

  material: string;
  materialId: number;
  materialTypeId: number;

  finish: string;
  finishId: number;

  tolerance: EnquiryPartTolerance;
  toleranceTitle: string;

  surfaceTitle: string;
  surfaceRoughness: EnquiryPartSurface;

  threadingQuantity: number;
  hardwareQuantity: number;

  markings: EnquiryPartMarking[];
  partMarkings: EnquiryPartMarking[];

  geometryReceived: boolean;

  hasPreQuote: boolean;
  preQuotedById: number;
  preQuotedByIdOption: any;
  preQuotedAt: string;
  preQuotedComment: string;
  preQuotedTicket: string;

  rfqRequired: boolean;
  rfqNeedEngineer: boolean;
  rfqNeedInfo: boolean;
  rfqUrgent: boolean;
  rfqRequiredByName: string;
  rfqOwnerId: number;
  rfqOwnerIdOption: any;
  rfqRequiredAt: string;
  rfqComment: string;
  rfqDeadline: string;
  rfqUrgency: string;

  manualCost: number;
  manualCostPerPiece: number;

  xometryPrice: number;
  autoquotedPrice: number;
  expressPrice: number;
  expressPricePerPiece: number;
  manualPrice: string;
  manualPricePerPiece: number;
  primaryPrice: number;
  primaryPriceMoney: Money;
  price: number;
  priceMoney: Money;
  currency?: string;
  pricePerPiece: number;
  pricePerPieceMoney: Money;
  priceExpired: boolean;
  bulkPrices?: any;
  quantityOptions: QuantityOptions[];

  priceValidTill?: string;
  priceValidTillDefault?: string;
  priceValidTillUpdatedAt?: string;
  priceValidTillUpdatedByName?: string;

  xometryLeadTime: number;
  autoquotedLeadTime: number;
  manualLeadTime: number;
  primaryLeadTime: number;
  expressLeadTime: number;
  leadTime: number;
  leadTimeTerms: string;
  dbLeadTime: number;

  leadDate: string;
  primaryLeadDate: string;
  expressLeadDate: string;

  hasExpress: boolean;
  optionSelected: EnquiryPartOption;

  samplesNeeded: number;
  gauge?: EnquiryGauge;
  samplesQuantity: number | null;
  samplesComment: string | null;
  measurementProtocolNeeded: string;
  measurementProtocol: OmsEnquiryMeasurementProtocol | null;

  customMaterial: string;
  customFinish: string;

  warnings: EnquiryError[];
  errors: EnquiryError[];

  created: string;
  updated: string;

  priceOptions?:
    | (Partial<Record<EnquiryPartPriceOptionType, EnquiryPartPriceOption | null>> & { ruleId?: number })
    | null;

  drawingsReworkTask?: string;
  drawingsReworkedBy?: { label: string; email: string };

  similarSyncAt: string;
  similarPartsCount: number;
  similarParts: EnquirySimilarPart[];

  tags: string[];
  shiftTagIds: number[];

  files: EnquiryPartFile[];
  priceExpiresAt?: string;
  productionRemark?: string;
  layerThickness?: number;
  resolution?: string;
  finishColor?: { id: number; name: string };
  finishCustomColor?: string;
  // Legacy fields for fallback compatibility
  color?: { id: number; name: string };
  customColor?: string;
  processColor?: { id: number; name: string };
  processCustomColor?: string;
  discountPrice?: string;
  discountPriceMoney?: Money;
  appliedCouponValueMoney?: Money;
  transportMode: TransportMode;
  toolLife?: number;
  transmittance?: number;
}

export interface QuantityOptions {
  cost?: number;
  leadTime: number;
  costPerPiece?: number;
  quantity: number;
  price?: number;
  priceMoney?: Money;
}

export interface EnquirySimilarPart {
  id: number;
  name: string;
  rate: number;
  claimIds: number[];
  personId: number;
  personName: string;
  isSamePerson: boolean;
  hasManualPrice: boolean;
  isExpress: boolean;
  dimensions: EnquiryPartDimensions;
  auditState: AuditState;

  dealId: number;
  dealPipedriveId: number;
  dealStatus: OverallStageEnum;
  dealCreatedAt: string;

  providerId: number;
  providerName: string;
  providerCountry: string;

  quotePrice: number;
  quoteLeadTime: number;
  quoteQty: number;

  salePrice: number;
  saleLeadTime: number;
  saleQty: number;

  providerPrice: number;
  providerQty: number;

  material: string;
  customMaterial: string;
  finish: string;
  customFinish: string;
  toleranceTitle: string;
  surfaceTitle: string;

  files: EnquiryPartFile[];

  created: string;
  updated: string;
}

export interface EnquiryShippingAddress {
  id: number;
  zip: string;
  city: string;
  address: string;
  country: string;
  companyName: string;
  contactName: string;
  contactPhone: string;
}

export interface Enquiry {
  id: number;
  dealId: number;
  number: string;
  state: EnquiryStates;
  quotingState: EnquiryQuotingState;
  nextState: EnquiryStates;
  permittedStates: EnquiryStates[];
  failed: boolean;
  noQuote: boolean;
  shouldRfq: boolean;
  certificates: Certificates;
  isCanceled: boolean;
  personId: number;

  customerNote: string;
  internalComment?: string;

  coupon?: string;

  shippingCountry: string;
  appliedCouponCode: string;

  shippingCost: number;
  shippingCostFrozen: boolean;
  shippingBasis: string;
  shippingTerms: string;
  shippingAddress: EnquiryShippingAddress;

  massKg: number;
  volume: number;

  deliveryDate: string;
  leadTime: number;
  leadTimeTerms: string;
  leadTimeFrozen: boolean;
  latestLeadTime: number;
  latestPartLeadDate: string;

  partsValue: number;
  minOrderAddition: number;
  appliedCouponValue: number;
  appliedCouponReadonly: boolean;
  prepayDiscount: number;
  totalAmountNet: number;
  totalAmountBrut: number;
  vatRate: number;
  vatAmount: number;

  updated: string;
  created: string;

  billingAccount?: {
    id: string;
    name: string;
    zip: string;
    city: string;
    address: string;
    country: string;
    accountType: string;
  };

  auditRequested: boolean;
  moderationRequested: boolean;

  parts: EnquiryPart[];
  additionalServices: EnquiryAdditionalService[];

  assignedEng: number;
  assignedEngUser?: User | null;
  assignedSa: number;
  assignedSaUser?: User | null;
  engineerName: string;
  salesAssistantName: string;
  kamModerationRequired: boolean;

  currentlyViewing: ICurrentlyViewing[];

  orderedAt: string | null;
  orderedBy: {
    type: 'Person' | 'User';
    name: string;
  } | null;

  xometryVatNumber?: ICachedXometryVatNumber;
  currency?: string;
  calculations?: IEnquiry['calculations'];
}

export interface NewEnquiryPart {
  _uid: string;
  name: string;
  materialId: number;
  finishId: number;
  processId: number;
  processColorId?: number;
  processCustomColor?: string;
  quantity: number;
  customMaterial: string;
  customFinish: string;
  finishColorId?: number;
  finishCustomColor?: string;
  notes?: string;
  fileIds: number[];
}

export interface EnquiryListItem {
  id: number;
  name: string;
  state: string;
}
